import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  deletePlannedLesson,
  previewPlannedLessonDeletion,
} from '@platform/client';
import { deletionPreviewSchema } from '@platform/schema/curriculum';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { describeApiError } from '../api-error';

import type { PlannedLesson } from '@platform/schema/curriculum';

/**
 * Spec §9: what removing a planned lesson removes, before it does. The API refuses
 * approved or published content and says why; this shows that refusal, and offers
 * archiving a draft lesson only as a separate, explicit choice.
 */
export function RemoveDialog({
  lesson,
  onClose,
  onRemoved,
}: {
  lesson: PlannedLesson | null;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const queries = useQueryClient();
  const [archiveDraft, setArchiveDraft] = useState(false);
  const lessonId = lesson?.lessonId;

  const preview = useQuery({
    queryKey: ['curriculum', 'lessons', 'deletion', lessonId],
    enabled: lessonId !== undefined,
    staleTime: 0,
    queryFn: async () =>
      deletionPreviewSchema.parse(
        (
          await previewPlannedLessonDeletion({
            path: { lessonId: lessonId ?? '' },
            throwOnError: true,
          })
        ).data,
      ),
  });
  const remove = useTrackedMutation(`Remove ${lessonId ?? ''}`, {
    mutationFn: () =>
      deletePlannedLesson({
        path: { lessonId: lessonId ?? '' },
        query: { archiveDraft: archiveDraft ? 'yes' : 'no' },
        throwOnError: true,
      }),
    onSuccess: async () => {
      onRemoved();
      await queries.invalidateQueries({ queryKey: ['curriculum'] });
    },
  });
  const close = (): void => {
    setArchiveDraft(false);
    remove.reset();
    onClose();
  };

  const data = preview.data;
  const hasDraft =
    data?.linkedLessons.some((one) => one.status === 'DRAFT') === true;

  return (
    <Dialog open={lesson !== null} onClose={close} fullWidth maxWidth="sm">
      <DialogTitle>Remove from curriculum?</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {lesson !== null && (
            <Typography>
              {lesson.lessonId} · {lesson.name}
              <br />
              {lesson.course} › {lesson.module}
            </Typography>
          )}
          {preview.isError && (
            <Alert severity="error">{describeApiError(preview.error)}</Alert>
          )}
          {data !== undefined && (
            <>
              <Typography>
                Removes {String(data.lessons)} planned lesson from the plan. Its
                Lesson ID is never given to another lesson.
              </Typography>
              {data.cancelledBuilds.length > 0 && (
                <Typography>Its queued build is cancelled first.</Typography>
              )}
              {data.linkedLessons.length === 0 ? (
                <Typography color="text.secondary">
                  Nothing in the app was built from it.
                </Typography>
              ) : (
                <Stack spacing={0.5}>
                  <Typography>Built from it in the app:</Typography>
                  <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                    {data.linkedLessons.map((one) => (
                      <li key={one.id}>
                        {one.title} ({one.status.toLowerCase()})
                      </li>
                    ))}
                  </Stack>
                </Stack>
              )}
              {data.blocked ? (
                <Alert severity="error">{data.message}</Alert>
              ) : (
                <>
                  <Typography color="text.secondary">
                    {archiveDraft
                      ? 'The plan row goes, and its draft lesson is archived.'
                      : 'Only the plan row goes. The app keeps everything built from it.'}
                  </Typography>
                  {hasDraft && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={archiveDraft}
                          onChange={(event) => {
                            setArchiveDraft(event.target.checked);
                          }}
                        />
                      }
                      label="Also archive its draft lesson in the app"
                    />
                  )}
                  {archiveDraft && (
                    <Typography variant="body2" color="text.secondary">
                      An archived lesson can be restored from Lessons.
                      {data.leftEmpty.map(
                        (module) =>
                          ` Module ${module} will be left with no lessons.`,
                      )}
                    </Typography>
                  )}
                </>
              )}
            </>
          )}
          {remove.isError && (
            <Alert severity="error">{describeApiError(remove.error)}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={data === undefined || data.blocked || remove.isPending}
          onClick={() => {
            remove.mutate();
          }}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}
